import { Result } from 'libs/end-to-end/src/result';
import { WithMeta } from 'libs/end-to-end/src/result';
import { errResult } from 'libs/end-to-end/src/result';
import { okResult } from 'libs/end-to-end/src/result';
import { withMeta } from 'libs/end-to-end/src/result';
import { S2SafeParsable } from 'libs/end-to-end/src/interfaces';
import { AxiosResponse } from 'axios';
import { AxiosResponseHeaders } from 'axios';
import { RawAxiosResponseHeaders } from 'axios';
import { jsonParse } from 'libs/end-to-end/src/utils/utility/json-parse';
import { UnknownObject } from 'libs/end-to-end/src/generic';
import { createZodParseError } from 'libs/end-to-end/src/utils/utility/create-zod-parse-error';
import { ResponseToJsonError } from 'libs/end-to-end/src/types/errors/response-to-json-error';

export const axiosResponseToJson = <TSchema>(props: {
  response: AxiosResponse<string>;
  schema: S2SafeParsable<unknown, TSchema>;
}): WithMeta<Result<TSchema, ResponseToJsonError>, unknown> => {
  const { response, schema } = props;

  const anyParseResult = jsonParse(response.data, UnknownObject);

  if (anyParseResult.resultType === 'err') {
    return withMeta(errResult(anyParseResult.error), null);
  }

  const resultAny = anyParseResult.value;
  const parseResult = schema.safeParse(resultAny);

  if (!parseResult.success) {
    return withMeta(errResult(createZodParseError(parseResult)), resultAny);
  }

  return withMeta(okResult(parseResult.data), resultAny);
};

export const axiosResponseToJsonUnchecked = (props: {
  response: AxiosResponse<string>;
}): Result<unknown, ResponseToJsonError> => {
  const { response } = props;

  const anyParseResult = jsonParse(response.data, UnknownObject);

  if (anyParseResult.resultType === 'err') {
    return errResult(anyParseResult.error);
  }

  return okResult(anyParseResult.value);
};

type HeadersLike = RawAxiosResponseHeaders | AxiosResponseHeaders;

export const readResponseHeader = (
  headers: HeadersLike,
  key: string,
): string | null => {
  if (!headers) {
    return null;
  }

  const headerByIndex: unknown = headers[key];

  if (headerByIndex && typeof headerByIndex === 'string') {
    return headerByIndex;
  }

  return null;
};

export const readAllResponseHeaders = (
  headers: HeadersLike,
): Record<string, string> => {
  const result: Record<string, string> = {};

  if (typeof headers !== 'object' || !headers) {
    return result;
  }

  const entries = Object.entries(headers);

  for (const [key, value] of entries) {
    if (typeof value === 'string') {
      result[key] = value;
    } else if (typeof value === 'number') {
      result[key] = value.toString();
    } else if (Array.isArray(value) && value.length > 0) {
      let fullValue = '';

      for (const item of value) {
        const itemUnk: unknown = item;

        if (typeof itemUnk === 'string') {
          fullValue += itemUnk;
        } else if (typeof itemUnk === 'number') {
          fullValue += itemUnk.toString();
        }
      }

      result[key] = fullValue;
    }
  }

  return result;
};
